import Card from "components/v2/Card/Card";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";

import { OrganisationSection } from "./sections/OrganisationSection";
import { AttachmentsSection } from "./sections/AttachmentsSection";
import { PackingListSection } from "./sections/PackingListSection";
import { ProvidersSection } from "./sections/ProvidersSection";
import { ScheduleSection } from "./sections/ScheduleSection";
import { ServiceSection } from "./sections/ServiceSection";
import { SHIPMENT_DETAILS_TEST_IDS } from "./testIds";
import type { ShipmentDetailsSheetViewModel, UserType } from "./types";
import "./ShipmentDetailsSheet.scss";

type ShipmentDetailsSheetProps = {
  viewModel: ShipmentDetailsSheetViewModel;
  loadingData?: boolean;
  userType?: UserType;
  isQuickRequest?: boolean;
};

export default function ShipmentDetailsSheet({
  viewModel,
  loadingData = false,
  userType = "shipper",
  isQuickRequest = false,
}: ShipmentDetailsSheetProps) {
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);
  const { data: shipmentBasic } = useGetShipmentBasic(viewModel.shipmentId, {
    enabled: !!viewModel.shipmentId && !loadingData && !isQuickRequest,
    refetchOnMount: false,
  });
  const { data: shipmentDetails } = useGetShipmentDetails(viewModel.shipmentId, true, {
    enabled: !!viewModel.shipmentId && isCustomBroker && !loadingData && !isQuickRequest,
    refetchOnMount: false,
  });
  const paymentLocationReference =
    viewModel.organisation.paymentLocationReference ??
    (shipmentBasic?.organization?.payment_location_reference as string | undefined);
  const isAutomatedBooking = Boolean(shipmentBasic?.shipment?.is_automated_booking);
  const winningProvider = shipmentDetails?.organization?.provider_company as string | undefined;

  if (loadingData) {
    return (
      <Card borderRadius="md" shadow="xs">
        <div
          className="shipment-details-sheet shipment-details-sheet--loading"
          data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SKELETON}
        >
          <div className="shipment-details-sheet__skeleton-bar" />
          <div className="shipment-details-sheet__skeleton-bar" />
          <div className="shipment-details-sheet__skeleton-bar" />
        </div>
      </Card>
    );
  }

  return (
    <Card borderRadius="md" shadow="xs">
      <div className="shipment-details-sheet" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SHEET}>
        <OrganisationSection
          organisation={viewModel.organisation}
          userType={userType}
          paymentLocationReference={paymentLocationReference}
          isAutomatedBooking={isAutomatedBooking}
        />
        <ServiceSection
          service={viewModel.service}
          isCustomBroker={isCustomBroker}
          winningProvider={winningProvider}
          paymentLocationReference={paymentLocationReference}
        />
        {viewModel.providers && <ProvidersSection providers={viewModel.providers} />}
        <ScheduleSection schedule={viewModel.schedule} />
        <PackingListSection
          packing={viewModel.packing}
          isMultistop={(viewModel.schedule.stops?.length ?? 0) > 2}
        />
        <AttachmentsSection shipmentId={viewModel.shipmentId} isQuickRequest={isQuickRequest} />
      </div>
    </Card>
  );
}
